import { Alert, Anchor, Badge, Button, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { formatDate, formatHours } from '../../lib/format';
import type { StaffMember } from '../staff/PersonDetailsPanel';
import { ScheduleEditorModal } from '../staff/ScheduleEditorModal';
import {
  DAY_SHORT,
  describeWeekTimes,
  inWeekOrder,
  NO_WORKING_WEEK_COSTS,
  SCHEDULES_PATH,
  type StaffSchedule,
  scheduleComingUp,
  scheduleInForce,
  schedulesByStaff,
  summarize,
  weekMinutes,
} from '../staff/schedule';

const MEMBERS_PATH = '/admin/v1/staff/members';

/**
 * Where the whole roster's working weeks are read and set.
 *
 * The editor has always existed and always worked. It was reachable only by opening Staff, opening
 * a person, and scrolling to a section between their PIN and their roles — so the owner's account
 * of it was that he could not set up the schedule at all, and every fortnight he paid for that in
 * typed sign-off reasons. A door nobody finds is a door that is not there.
 */
export const WORKING_WEEKS_TAB = '/payroll?tab=weeks';

/**
 * A link that lands on this list, optionally with one person's editor already open, and optionally
 * carrying the date the week has to count from.
 *
 * `from` is the period the reader was standing in. Without it the editor opens on today whatever
 * was on the screen behind it — so a week set to answer a flag on a fortnight already worked is
 * written from a date after that fortnight ended, the flag survives the fix, and nothing says why.
 * It is carried without a staff id as well, because a link naming four people lands on this list
 * and the editor gets opened here, once per person, from the same period.
 */
export const workingWeekLink = (staffId: string | null, from?: string | null) => {
  const query = new URLSearchParams();
  if (staffId) query.set('staff', staffId);
  if (from) query.set('from', from.slice(0, 10));
  const suffix = query.toString();
  return suffix ? `${WORKING_WEEKS_TAB}&${suffix}` : WORKING_WEEKS_TAB;
};

/** One person as this list reads them: who they are, the week in force, and any change dated ahead. */
interface RosterRow {
  member: StaffMember;
  current?: StaffSchedule;
  upcoming?: StaffSchedule;
}

/** The week itself, or the plainest possible statement that there is not one. */
function WeekCell({ row }: { row: RosterRow }) {
  const { current, upcoming } = row;
  const ahead = upcoming && (
    <Text size="xs" c="blue.7">
      Changes to {summarize(upcoming.pattern)} on {formatDate(upcoming.effectiveFrom)}
    </Text>
  );

  // Never set at all, which is the gap this list exists to close.
  if (!current) {
    return (
      <Stack gap={2}>
        <Badge color="yellow" variant="light">
          Not set
        </Badge>
        {ahead}
      </Stack>
    );
  }

  /*
   * A row in force holding no days is not the same absence: it is somebody moved to on-call, and
   * saying so is the whole reason an empty pattern can be saved. The hours screen still flags their
   * days, because a schedule with no days in it cannot judge one — but the owner decided this, and
   * reporting his own decision back to him as an oversight is how a warning stops being read.
   */
  if (current.pattern.length === 0) {
    return (
      <Stack gap={2}>
        <Text size="sm" c="dimmed">
          No fixed schedule since {formatDate(current.effectiveFrom)}
        </Text>
        {ahead}
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Text size="sm" fw={600}>
        {inWeekOrder(current.pattern)
          .map((d) => DAY_SHORT[d.weekday])
          .join(', ')}
      </Text>
      {describeWeekTimes(current.pattern).map((line) => (
        <Text key={line} size="xs" c="dimmed">
          {line}
        </Text>
      ))}
      <Text size="xs" c="dimmed">
        {current.effectiveUntil
          ? `Working week ${formatDate(current.effectiveFrom)} – ${formatDate(current.effectiveUntil)}`
          : `In force since ${formatDate(current.effectiveFrom)}`}
      </Text>
      {ahead}
    </Stack>
  );
}

/**
 * Everybody's working week, in one place, beside the hours it decides.
 *
 * Two requests for the whole team: the roster, and every schedule ever written. The schedules
 * endpoint answers without a staff id precisely so this does not become one request per head — and
 * the same cached answer is what lets the editor offer to copy a week somebody already works.
 */
export function WorkingWeeksPanel() {
  /*
   * Which person's editor is open lives in the address, so the hours screen can link straight into
   * it. That link is the point: the flag that says a working week is missing is read on another
   * tab, and an owner told to go and find the right person on a different screen does not.
   */
  const [params, setParams] = useSearchParams();
  const openFor = params.get('staff');
  /*
   * The date a week opened from here has to count from — the start of the period whose hours sent
   * the reader over. Shape-checked rather than trusted: it reaches a date field and then an
   * effective-from column, and an address is typed by hand often enough that "from=last%20june"
   * must seed nothing rather than seed nonsense. It deliberately outlives the editor closing, so
   * the second and third person on a list of four are written for the same period as the first.
   */
  const askedFrom = params.get('from');
  const openFrom = askedFrom && /^\d{4}-\d{2}-\d{2}$/.test(askedFrom) ? askedFrom : null;
  const setOpenFor = (staffId: string | null) => {
    setParams(
      (previous) => {
        const merged = new URLSearchParams(previous);
        if (staffId) merged.set('staff', staffId);
        else merged.delete('staff');
        return merged;
      },
      { replace: true },
    );
  };

  const staff = useQuery({
    queryKey: listKey(MEMBERS_PATH),
    queryFn: () => rawApi<StaffMember[]>('GET', MEMBERS_PATH),
  });
  const schedules = useQuery({
    queryKey: listKey(SCHEDULES_PATH),
    queryFn: () => rawApi<StaffSchedule[]>('GET', SCHEDULES_PATH),
  });

  // The BAR's calendar, not the reader's browser: `useIslandToday` carries the reasoning and the
  // six times a UTC-sliced date has answered tomorrow from six in the evening.
  const today = useIslandToday();
  const byStaff = schedulesByStaff(schedules.data ?? []);

  /**
   * Both answers in hand, which is not the same as the roster being in hand.
   *
   * These are two independent requests and the schedules are the slower of them: guarding on the
   * roster alone rendered this table with the schedule map still empty, so every person read "Not
   * set" and the alert named THE WHOLE TEAM — a false accusation on the one screen built to report
   * that gap, and a link straight into an editor holding an empty week for somebody who has one.
   * A missing answer is not an answer of missing, so nothing here reads a person's week until the
   * request that carries every person's week has come back.
   */
  const answered = staff.data !== undefined && schedules.data !== undefined;

  /*
   * People who still work here. A leaver's working week is history — it is still on file and still
   * judges the timesheets it was in force for, and setting one now is not a job anybody has.
   */
  const rows: RosterRow[] = (staff.data ?? [])
    .filter((member) => member.status === 'active')
    .map((member) => {
      const history = byStaff.get(member.id) ?? [];
      return {
        member,
        current: scheduleInForce(history, today),
        upcoming: scheduleComingUp(history, today),
      };
    })
    .sort((a, b) => a.member.displayName.localeCompare(b.member.displayName));

  const neverSet = rows.filter((row) => !row.current);
  const onlyOneMissing = neverSet.length === 1 ? neverSet[0] : undefined;
  /*
   * Looked up in the whole roster rather than in the rows above, which are the people who still
   * work here. A link from the hours screen can name somebody who has since left — their last
   * fortnight is still being approved — and a link that opens nothing is worse than no link.
   *
   * Gated on both answers, because this is the deep link's own path and the editor seeds its grid
   * once, on mount. Mounted a moment early it seeds an EMPTY week for somebody who has one, offers
   * the "no longer has a fixed schedule" warning about them, and — since the modal is keyed on the
   * person and the person has not changed — keeps that empty grid after their real week arrives.
   * Saving from there would wipe a working week that took a fortnight of flagged hours to notice.
   */
  const editingMember = answered ? staff.data?.find((member) => member.id === openFor) : undefined;
  /*
   * The grid opens on the week in force on the date being written for, so everything in the editor
   * — the days, the times, the week offered from somebody else — describes the same moment. Failing
   * that it opens on the week they work now: a backdated row seeded EMPTY for somebody who has a
   * schedule is one click from recording that they have none, and retyping a week by hand is how
   * the copy-from-somebody-else affordance came to exist in the first place.
   */
  const editingHistory = editingMember ? (byStaff.get(editingMember.id) ?? []) : [];
  const editingPattern = editingMember
    ? (scheduleInForce(editingHistory, openFrom ?? today) ?? scheduleInForce(editingHistory, today))?.pattern
    : undefined;

  return (
    <Paper withBorder p="md" radius="md">
      <div>
        <Title order={5}>Working weeks</Title>
        <Text size="sm" c="dimmed" mb="md">
          The days and hours each person is expected to work. Approving hours compares what they actually
          worked against this, so it is what makes overtime and a short day mean anything.
        </Text>
      </div>

      {staff.isError || schedules.isError ? (
        <Alert color="red" title="Could not load working weeks">
          Nothing has been lost — this is only a display problem.
        </Alert>
      ) : !answered ? (
        <Text size="sm" c="dimmed" fs="italic">
          Reading the team…
        </Text>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          Nobody works here yet. Add your team under Staff.
        </Text>
      ) : (
        <>
          {neverSet.length > 0 && (
            <Alert
              color="yellow"
              variant="light"
              mb="md"
              title={
                onlyOneMissing
                  ? `Nobody has said which days ${onlyOneMissing.member.displayName} works`
                  : `${neverSet.length} people have no working week set`
              }
            >
              <Text size="sm">
                {NO_WORKING_WEEK_COSTS} Set the week below and all three come back on their own.
              </Text>
              {!onlyOneMissing && (
                <Text size="sm" mt={4}>
                  {neverSet.map((row) => row.member.displayName).join(', ')}.
                </Text>
              )}
            </Alert>
          )}

          <Table.ScrollContainer minWidth={620}>
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Person</Table.Th>
                  <Table.Th>Working week</Table.Th>
                  <Table.Th>Hours a week</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <Table.Tr key={row.member.id}>
                    <Table.Td>
                      {/* Their own page, where their pay, roles and hours are. The week is set from
                          the button on this row; the name still goes where the person is. */}
                      <Anchor component={Link} to={`/staff/${row.member.id}`} fw={600}>
                        {row.member.displayName}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <WeekCell row={row} />
                    </Table.Td>
                    <Table.Td>
                      {row.current && row.current.pattern.length > 0 ? (
                        <Text size="sm" fw={600}>
                          {formatHours(weekMinutes(row.current.pattern))}
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Button
                        size="compact-sm"
                        variant={row.current ? 'subtle' : 'light'}
                        onClick={() => setOpenFor(row.member.id)}
                      >
                        {row.current ? 'Change' : 'Set working week'}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </>
      )}

      {editingMember && (
        // Keyed on the date as well as the person: the same name opened for a different period is a
        // different act, and the editor seeds its fields once.
        <ScheduleEditorModal
          key={`${editingMember.id}:${openFrom ?? today}`}
          staffId={editingMember.id}
          staffName={editingMember.displayName}
          currentPattern={editingPattern ?? []}
          initialEffectiveFrom={openFrom}
          onClose={() => setOpenFor(null)}
        />
      )}
    </Paper>
  );
}
